import clsx from 'clsx';

interface BookmarkRowSkeletonProps {
  condensedView?: boolean;
  showThumbnails?: boolean;
}

function BookmarkRowSkeleton({
  condensedView = false,
  showThumbnails = true,
}: BookmarkRowSkeletonProps) {
  // aria-hidden + data-testid so React tests can still target the root
  // without relying on a per-skeleton aria-label. The parent <section>
  // already exposes "Loading articles" to AT (BookmarkList.tsx), so each
  // skeleton row is decorative — putting aria-label on a div with the
  // implicit generic role would trigger axe's aria-prohibited-attr rule
  // and also produce N redundant announcements during loading.
  return (
    <div
      data-testid="bookmark-row-skeleton"
      aria-hidden="true"
      className={clsx('bookmark-item', condensedView && 'condensed', 'animate-pulse')}
    >
      <div className={clsx('flex', 'bookmark-content', condensedView && 'condensed')}>
        {/* Main content */}
        <div className={clsx('flex-1', condensedView && 'py-2')}>
          {/* Title */}
          <div className="mb-2">
            <div className="bg-surface-secondary h-6 w-4/5 rounded" />
          </div>

          {/* Meta information (domain, reading time) */}
          <div className="mb-2 flex gap-2">
            <div className="bg-surface-secondary h-4 w-24 rounded" />
            <div className="bg-surface-secondary h-4 w-16 rounded" />
          </div>

          {/* Description - only show in expanded view */}
          {!condensedView && (
            <div className="mb-2">
              <div className="bg-surface-secondary h-4 w-full rounded" />
              <div className="bg-surface-secondary mt-1 h-4 w-3/4 rounded" />
            </div>
          )}
        </div>

        {/* Thumbnail skeleton */}
        {showThumbnails && (
          <div
            className={clsx(
              'ml-4 flex-shrink-0',
              condensedView ? 'h-12 w-12' : 'h-20 w-20',
            )}
          >
            <div className="bg-surface-secondary h-full w-full rounded" />
          </div>
        )}
      </div>
    </div>
  );
}

export default BookmarkRowSkeleton;
